<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate, toApiDateTime } from '@yuksalish/ui'
import { useShopfloorApi } from '../composables/useShopfloorApi'
import type { HandoverRecord } from '../types/shopfloor'

const { t } = useI18n()
const { listHandovers, createHandover } = useShopfloorApi()

const records = ref<HandoverRecord[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const submitting = ref(false)

const showCreate = ref(false)
const form = reactive({
  shift: 'day',
  from: new Date().toISOString().slice(0, 16),
  to: new Date().toISOString().slice(0, 16),
  orders_handed_over: 0,
  issues_flagged: 0,
  attention_required: false,
  notes: '',
})

const totals = computed(() =>
  records.value.reduce(
    (acc, r) => ({
      orders: acc.orders + r.orders_handed_over,
      issues: acc.issues + r.issues_flagged,
      attention: acc.attention + (r.attention_required ? 1 : 0),
    }),
    { orders: 0, issues: 0, attention: 0 },
  ),
)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listHandovers({ page: 1, per_page: 100 })
    records.value = result.items
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(load)

function openCreate() {
  form.shift = 'day'
  form.from = new Date().toISOString().slice(0, 16)
  form.to = new Date().toISOString().slice(0, 16)
  form.orders_handed_over = 0
  form.issues_flagged = 0
  form.attention_required = false
  form.notes = ''
  actionError.value = ''
  actionMessage.value = ''
  showCreate.value = true
}

async function submitCreate() {
  actionError.value = ''
  if (form.orders_handed_over < 0 || form.issues_flagged < 0) {
    actionError.value = t('shopfloor.orderDetail.enterStageQuantity')
    return
  }
  submitting.value = true
  try {
    const created = await createHandover({
      shift: form.shift,
      from: toApiDateTime(form.from),
      to: toApiDateTime(form.to),
      orders_handed_over: Number(form.orders_handed_over) || 0,
      issues_flagged: Number(form.issues_flagged) || 0,
      attention_required: form.attention_required,
      notes: form.notes.trim() || undefined,
    })
    records.value = [created, ...records.value]
    showCreate.value = false
    actionMessage.value = t('shopfloor.handover.created', { id: created.id })
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('shopfloor.handover.eyebrow') }}</p>
      <h2>{{ t('shopfloor.handover.title') }}</h2>
      <p>{{ t('shopfloor.handover.description') }}</p>
    </div>
    <button class="btn-primary" @click="openCreate">{{ t('shopfloor.handover.createHandover') }}</button>
  </div>

  <!-- Totals -->
  <div v-if="!loading && !error && records.length" class="summary-row">
    <div class="summary-card">
      <span class="summary-value">{{ totals.orders }}</span>
      <span class="summary-label">{{ t('shopfloor.handover.ordersHandedOver') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ totals.issues }}</span>
      <span class="summary-label">{{ t('shopfloor.handover.issuesFlagged') }}</span>
    </div>
    <div class="summary-card" :class="{ attention: totals.attention > 0 }">
      <span class="summary-value">{{ totals.attention }}</span>
      <span class="summary-label">{{ t('shopfloor.handover.attentionRequired') }}</span>
    </div>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('shopfloor.handover.loading') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('shopfloor.state.retry') }}</button>
  </div>

  <!-- Empty -->
  <div v-else-if="records.length === 0" class="state-message">
    <p>{{ t('shopfloor.handover.empty') }}</p>
  </div>

  <!-- Cards -->
  <div v-else class="handover-list">
    <article v-for="r in records" :key="r.id" class="handover-card" :class="{ attention: r.attention_required }">
      <div class="card-header">
        <div>
          <h3>{{ t('shopfloor.handover.shift') }} · {{ t(`shopfloor.assignments.${r.shift === 'day' ? 'day' : 'night'}`) }}</h3>
          <p class="card-meta">{{ formatDisplayDateTime(r.from) }} → {{ formatDisplayDateTime(r.to) }}</p>
        </div>
        <span v-if="r.attention_required" class="attention-badge">{{ t('shopfloor.handover.attentionRequired') }}</span>
      </div>
      <div class="card-body">
        <div class="card-stat">
          <span class="stat-value">{{ r.orders_handed_over }}</span>
          <span class="stat-label">{{ t('shopfloor.handover.ordersHandedOver') }}</span>
        </div>
        <div class="card-stat">
          <span class="stat-value">{{ r.issues_flagged }}</span>
          <span class="stat-label">{{ t('shopfloor.handover.issuesFlagged') }}</span>
        </div>
      </div>
      <div class="card-footer">
        <span class="supervisor">{{ t('shopfloor.handover.supervisor') }}: {{ r.supervisor_name }}</span>
        <p v-if="r.notes" class="notes">{{ r.notes }}</p>
      </div>
    </article>
  </div>

  <!-- Create modal -->
  <div v-if="showCreate" class="modal-overlay" @click.self="showCreate = false">
    <form class="modal" @submit.prevent="submitCreate">
      <h3>{{ t('shopfloor.handover.createHandover') }}</h3>
      <label>{{ t('shopfloor.assignments.shift') }}
        <select v-model="form.shift">
          <option value="day">{{ t('shopfloor.assignments.day') }}</option>
          <option value="night">{{ t('shopfloor.assignments.night') }}</option>
        </select>
      </label>
      <label>{{ t('shopfloor.handover.from') }}
        <input v-model="form.from" type="datetime-local" required />
      </label>
      <label>{{ t('shopfloor.handover.to') }}
        <input v-model="form.to" type="datetime-local" required />
      </label>
      <label>{{ t('shopfloor.handover.ordersHandedOver') }}
        <input v-model.number="form.orders_handed_over" type="number" min="0" required />
      </label>
      <label>{{ t('shopfloor.handover.issuesFlagged') }}
        <input v-model.number="form.issues_flagged" type="number" min="0" required />
      </label>
      <label class="check">
        <input v-model="form.attention_required" type="checkbox" />
        {{ t('shopfloor.handover.attentionRequired') }}
      </label>
      <label>{{ t('shopfloor.handover.notes') }}
        <input v-model="form.notes" type="text" />
      </label>
      <small v-if="actionError" class="error">{{ actionError }}</small>
      <div class="modal-actions">
        <button type="button" class="btn-secondary" @click="showCreate = false">{{ t('shopfloor.handover.cancel') }}</button>
        <button type="submit" class="btn-primary" :disabled="submitting">
          {{ submitting ? t('shopfloor.handover.creating') : t('shopfloor.handover.create') }}
        </button>
      </div>
    </form>
  </div>

  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
</template>

<style scoped>
.page-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }
.heading-actions {
  display: flex;
  gap: 8px;
}
.btn-secondary {
  padding: 8px 16px;
  background: #fff;
  color: #27705d;
  border: 1px solid #27705d;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.btn-secondary:hover { background: #f0f5f3; }

.state-message {
  padding: 40px 0;
  text-align: center;
  color: #5f6b6a;
}
.state-message.error { color: #b33a3a; }

.handover-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.handover-card {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  padding: 20px;
}
.handover-card.attention { border-color: #f0c040; background: #fffdf5; }
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}
.card-header h3 { margin: 0; font-size: 16px; font-weight: 700; }
.card-meta { margin: 2px 0 0; font-size: 13px; color: #65736e; }
.attention-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  background: #fef3cd;
  color: #856404;
}
.card-body {
  display: flex;
  gap: 32px;
  margin-bottom: 16px;
}
.card-stat { text-align: center; }
.stat-value { display: block; font-size: 24px; font-weight: 800; color: #1a2b26; }
.stat-label { display: block; font-size: 11px; color: #65736e; text-transform: uppercase; font-weight: 600; }
.card-footer {
  border-top: 1px solid #f0f2f1;
  padding-top: 12px;
}
.supervisor { font-size: 12px; color: #65736e; font-weight: 600; }
.notes {
  margin: 6px 0 0;
  font-size: 13px;
  color: #1a2b26;
  font-style: italic;
}

.summary-row {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}
.summary-card {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  padding: 16px 24px;
  text-align: center;
}
.summary-card.attention { border-color: #f0c040; background: #fffdf5; }
.summary-value { display: block; font-size: 26px; font-weight: 800; color: #1a2b26; }
.summary-label { display: block; font-size: 11px; color: #65736e; text-transform: uppercase; font-weight: 600; margin-top: 2px; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 20, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal {
  width: 380px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 90vh;
  overflow-y: auto;
}
.modal h3 { margin: 0; font-size: 16px; font-weight: 700; }
.modal label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
  gap: 4px;
}
.modal input, .modal select {
  padding: 8px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
}
.modal .check { flex-direction: row; align-items: center; }
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.error { color: #b33a3a; font-size: 12px; }
.action-message {
  padding: 12px 16px;
  background: #dcefe7;
  color: #1f5c4b;
  border-radius: 8px;
  font-size: 13px;
  margin-top: 16px;
}

.btn-primary {
  display: inline-block;
  padding: 8px 20px;
  background: #27705d;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary:disabled { opacity: 0.5; cursor: default; }
.btn-primary:hover:not(:disabled) { background: #1f5c4b; }
</style>
